import api from './api';

export const uploadAnalysis = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/analyses', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

// LAKPED-13: onlyWithComparison filtra los análisis que tienen comparativa guardada
export const getAnalyses = async (skip = 0, limit = 10, onlyWithComparison = false, search = '') => {
  const comparison = onlyWithComparison ? '&with_comparison=true' : '';
  const query = search.trim() ? `&search=${encodeURIComponent(search.trim())}` : '';
  const response = await api.get(`/analyses?skip=${skip}&limit=${limit}${comparison}${query}`);
  return response.data;
};

export const getAnalysis = async (analysisHash, includeDeleted = false) => {
  const params = includeDeleted ? '?include_deleted=true' : '';
  const response = await api.get(`/analyses/${analysisHash}${params}`);
  return response.data;
};

export const getAnalysisResults = async (analysisHash) => {
  const response = await api.get(`/analyses/${analysisHash}/results`);
  return response.data;
};

export const downloadExcel = async (analysisHash, filename) => {
  const response = await api.get(`/analyses/${analysisHash}/excel`, {
    responseType: 'blob',
  });
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const deleteAnalysis = async (analysisHash) => {
  const response = await api.delete(`/analyses/${analysisHash}`);
  return response.data;
};

export const startAnalysis = async (analysisHash, steps = [1, 2, 3]) => {
  const response = await api.post(`/analyses/${analysisHash}/start-analysis`, { steps });
  return response.data;
};

export const getAnalysisProgress = async (analysisHash) => {
  const response = await api.get(`/analyses/${analysisHash}/progress`);
  return response.data;
};

export const getOperationsExtrasSchema = async () => {
  const response = await api.get('/operations/extras-schema');
  return response.data;
};

export const getAnalysisSimilars = async (analysisHash) => {
  const response = await api.get(`/analyses/${analysisHash}/similars`);
  return response.data;
};

export const calculateAnalysisSimilars = async (analysisHash, limit = 10, pageNumber = null, stage = null, alreadySearchedHashes = null, existingAboveCount = 0, existingBelowThreshold = null) => {
  const body = { limit };
  if (pageNumber !== null) body.page_number = pageNumber;
  if (stage !== null) body.stage = stage;
  if (alreadySearchedHashes) body.already_searched_hashes = alreadySearchedHashes;
  if (existingAboveCount > 0) body.existing_above_count = existingAboveCount;
  if (existingBelowThreshold) body.existing_below_threshold = existingBelowThreshold;
  const response = await api.post(`/analyses/${analysisHash}/similars/calculate`, body);
  return response.data;
};

export const detectAnnotations = async (analysisHash, page = 1) => {
  const response = await api.post(`/analyses/${analysisHash}/detect?page=${page}`);
  return response.data;
};

export const readManualCrops = async (crops) => {
  const response = await api.post('/analyses/read-crops', { crops });
  return response.data;
};

export const getDetections = async (analysisHash, page = 1) => {
  const response = await api.get(`/analyses/${analysisHash}/detections?page=${page}`);
  return response.data;
};

export const saveDetections = async (analysisHash, detections, page = 1) => {
  const response = await api.put(`/analyses/${analysisHash}/detections?page=${page}`, { detections });
  return response.data;
};

export const findFileInMcp = async (analysisHash) => {
  const response = await api.get(`/analyses/${analysisHash}/find-in-mcp`);
  return response.data;
};

export const deleteDetections = async (analysisHash) => {
  const response = await api.delete(`/analyses/${analysisHash}/detections`);
  return response.data;
};

export const getAnalysesWithDetections = async (skip = 0, limit = 20) => {
  const response = await api.get(`/analyses/with-detections?skip=${skip}&limit=${limit}`);
  return response.data;
};

export const downloadDetectionExcel = async (analysisHash, page = 1) => {
  const response = await api.get(`/analyses/${analysisHash}/detections/excel?page=${page}`, {
    responseType: 'blob',
  });

  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename=(.+)/);
  const filename = match ? match[1] : `detecciones_${analysisHash}.xlsx`;

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
// NUBBI-99 — Override manual de is_conical
export const updateConicalClassification = async (analysisHash, isConical) => {
  const response = await api.put(`/analyses/${analysisHash}/conical`, { is_conical: isConical });
  return response.data;
};

// NUBBI-102 — Búsqueda por elementos (solo cuentas con elements_search_enabled)
export const getElementsLayout = async (imageB64) => {
  const response = await api.post('/analyses/elements/layout', { image_b64: imageB64 }, { timeout: 300000 });
  return response.data;
};

const ELEMENTS_POLL_INTERVAL_MS = 2500;
const ELEMENTS_POLL_MAX_ATTEMPTS = 240;

export const searchByElements = async (imageB64, boxes, excludeHash = null, filters = null) => {
  const start = await api.post(
    '/analyses/elements/search-async',
    { image_b64: imageB64, boxes, exclude_hash: excludeHash, filters },
    { timeout: 60000 }
  );
  if (!start.data.task_id) return start.data;

  const { task_id: taskId, filtered } = start.data;
  for (let attempt = 0; attempt < ELEMENTS_POLL_MAX_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, ELEMENTS_POLL_INTERVAL_MS));
    const status = await api.get(`/analyses/elements/search-status/${taskId}`);
    if (status.data.state === 'SUCCESS') return { ...status.data.result, filtered };
    if (status.data.state === 'FAILURE') {
      throw new Error(status.data.error || 'Error en la búsqueda por elementos');
    }
  }
  throw new Error('La búsqueda por elementos ha tardado demasiado');
};

const compareElementZones = async (ruta, queryImageB64, candidateImageB64, timeout = 180000) => {
  const response = await api.post(
    ruta,
    { query_image_b64: queryImageB64, candidate_image_b64: candidateImageB64 },
    { timeout }
  );
  return response.data;
};

export const explainElementsDiff = (queryImageB64, candidateImageB64) =>
  compareElementZones('/analyses/elements/explain-diff', queryImageB64, candidateImageB64);

// La criba son ~1 s por candidato: si a los 45 no ha contestado, es que algo va mal y se da por
// perdida esa tarjeta en vez de dejar la rueda girando tres minutos.
export const screenElementMatch = (queryImageB64, candidateImageB64) =>
  compareElementZones('/analyses/elements/screen-match', queryImageB64, candidateImageB64, 45000);

export const explainNoElementMatches = (queryImageB64, candidateImageB64) =>
  compareElementZones('/analyses/elements/explain-no-matches', queryImageB64, candidateImageB64);

// LAKPED-14 — los planos que llevan esa misma referencia de pieza
export const getDrawingsByReference = async (reference, excludeHash = null) => {
  // La referencia va como parámetro, no en la ruta: el 15% llevan barra ("772510011/WIP030")
  // y dentro de la ruta se pierden aunque se codifiquen.
  const params = { reference };
  if (excludeHash) params.exclude_hash = excludeHash;
  const response = await api.get('/analyses/by-reference', { params });
  return response.data;
};

export const getStoredElements = async (analysisHash) => {
  const response = await api.get(`/analyses/elements/stored/${analysisHash}`);
  return response.data;
};

export const replaceElements = async (analysisHash, pageNumber, imageB64, elements) => {
  const response = await api.post(
    '/analyses/elements/replace',
    { analysis_hash: analysisHash, page_number: pageNumber, image_b64: imageB64, elements },
    { timeout: 300000 }
  );
  return response.data;
};

// NUBBI-102 (anexo) — persistencia de la comparativa
export const saveComparisonCache = async (analysisHash, snapshot) => {
  const response = await api.put(`/analyses/${analysisHash}/comparison-cache`, snapshot);
  return response.data;
};

export const getComparisonCache = async (analysisHash) => {
  const response = await api.get(`/analyses/${analysisHash}/comparison-cache`);
  return response.data;
};
